import { useEffect, useState, type ReactNode, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Button, Tooltip } from 'antd';
import { MinusOutlined, PlusOutlined } from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faPen, faXmark } from '@fortawesome/free-solid-svg-icons';
import {
  useLegendConfig,
  MIN_SCALE_SIZE,
  MAX_SCALE_SIZE,
} from '../../../../hooks/SimViewer/useLegendConfig';
import { COLORS } from '../../../../constants/color';
import { alpha } from '../../../../utils/color';
import { InfoPopoverButton } from '../../../InfoPopoverButton';

const isValidScaleSize = (value: string): boolean => {
  return (
    value !== '' &&
    MIN_SCALE_SIZE <= Number(value) &&
    Number(value) <= MAX_SCALE_SIZE
  );
};

const LegendEditActions = (): ReactNode => {
  const { t } = useTranslation();
  const {
    scaleType,
    scaleSize,
    customRange,
    draftCustomRange,
    isEditingLegend,
    isValidDraftCustomRange,
    setScaleSize,
    setCustomRange,
    setDraftCustomRange,
    setIsEditingLegend,
  } = useLegendConfig();

  const [localScaleSize, setLocalScaleSize] = useState<string>(
    String(scaleSize)
  );

  // 外部から値が変更されたときにローカルの状態も更新する
  useEffect((): void => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLocalScaleSize(String(scaleSize));
  }, [scaleSize]);

  const canConfirmCustomRange = draftCustomRange.every(
    (range: [number, number], index: number): boolean => {
      return isValidDraftCustomRange(index, range);
    }
  );

  if (scaleType !== 'custom') {
    return (
      <InputContainer
        data-error={!!localScaleSize && !isValidScaleSize(localScaleSize)}
      >
        <InfoPopoverButton
          content={t('additionalInfos.scale', { returnObjects: true })}
          style={{ position: 'absolute', top: -6, right: -26 }}
        />
        <PlusMinusButton
          type="button"
          name="classes-minus"
          disabled={scaleSize <= MIN_SCALE_SIZE}
          onClick={(): void => {
            if (MIN_SCALE_SIZE < scaleSize) {
              setLocalScaleSize((scaleSize - 1).toString());
              setScaleSize(scaleSize - 1);
            }
          }}
        >
          <MinusOutlined />
        </PlusMinusButton>
        <NumberInput
          name="classes"
          type="text"
          inputMode="numeric"
          value={localScaleSize}
          onChange={(event: ChangeEvent<HTMLInputElement>): void => {
            const value = event.target.value.trim().replace(/[^0-9]/g, '');
            setLocalScaleSize(value);
            if (isValidScaleSize(value)) {
              setScaleSize(Number(value));
            }
          }}
          onBlur={(): void => {
            // 不正な値が入力された場合は元の値に戻す
            if (!isValidScaleSize(localScaleSize)) {
              setLocalScaleSize(scaleSize.toString());
            }
          }}
        />
        <PlusMinusButton
          type="button"
          name="classes-plus"
          disabled={scaleSize >= MAX_SCALE_SIZE}
          onClick={(): void => {
            if (scaleSize < MAX_SCALE_SIZE) {
              setLocalScaleSize((scaleSize + 1).toString());
              setScaleSize(scaleSize + 1);
            }
          }}
        >
          <PlusOutlined />
        </PlusMinusButton>
      </InputContainer>
    );
  }
  if (!isEditingLegend) {
    return (
      <Tooltip placement="top" title={t('actions.edit')}>
        <Button
          icon={<FontAwesomeIcon icon={faPen} color="var(--primary-color)" />}
          type="text"
          shape="circle"
          onClick={(): void => {
            setIsEditingLegend(true);
          }}
        />
      </Tooltip>
    );
  }
  return (
    <>
      <Tooltip placement="top" title={t('actions.cancel')}>
        <Button
          icon={<FontAwesomeIcon icon={faXmark} color="var(--primary-color)" />}
          type="text"
          shape="circle"
          onClick={(): void => {
            // 編集を終了して下書きの値をリセットする
            setIsEditingLegend(false);
            setDraftCustomRange(customRange);
          }}
        />
      </Tooltip>
      <Tooltip placement="top" title={t('actions.confirm')}>
        <Button
          icon={
            <FontAwesomeIcon
              icon={faCheck}
              color={canConfirmCustomRange ? 'var(--primary-color)' : '#D2D2D2'}
            />
          }
          type="text"
          shape="circle"
          disabled={!canConfirmCustomRange}
          onClick={(): void => {
            // 編集を終了して下書きの値を反映する
            setIsEditingLegend(false);
            setCustomRange(draftCustomRange);
          }}
        />
      </Tooltip>
    </>
  );
};

const InputContainer = styled.div`
  position: relative;
  padding: 2px;
  margin: 0 22px 0 0;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
  background-color: ${COLORS.white};
  border: 1px solid ${alpha(COLORS.lightGray, 0.4)};
  border-radius: 2px;
  &[data-error='true'] {
    border-color: ${COLORS.red};
  }
`;

const NumberInput = styled.input`
  width: 24px;
  height: 22px;
  padding: 0;
  text-align: center;
  border: none;
  &:focus {
    outline: none;
  }
`;

const PlusMinusButton = styled.button`
  width: 16px;
  height: 16px;
  padding: 0;
  background-color: ${COLORS.transparent};
  border: none;
  border-radius: 2px;
  font-size: 12px;
  cursor: pointer;
  &:disabled {
    cursor: not-allowed;
    opacity: 0.3;
  }
`;

export { LegendEditActions };
